import { useCallback, useEffect, useRef, useState } from 'react';
import { queryTable } from '../api/harperClient';
import { useHarperSubscription } from '../context/HarperWSContext';
import type { Order } from '../types/order';

/**
 * src/hooks/useCustomerOrders.ts - Fetch customer orders.
 * WP-ATTICDEV-005-ORDER, Tasks 1+7 - AC-003, AC-007
 *
 * 1. Resolve customer email -> UUID via Customer table (same pattern as useCrateSubscription).
 * 2. Query Order table filtered by customer_id (AC-003: never returns other customers orders).
 * 3. Sort by scheduled_date descending - most recent first (AC-002).
 * State: loading | error | success discriminated union.
 */

interface CustomerRecord {
  uuid: string;
  email: string;
}

type OrdersState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'success'; orders: Order[] };

export interface UseCustomerOrdersResult {
  orders: Order[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCustomerOrders(
  customerEmail: string | null,
  authToken: string | null,
): UseCustomerOrdersResult {
  const [state, setState] = useState<OrdersState>({ phase: 'loading' });
  const customerUuidRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);

  const fetchOrders = useCallback(async () => {
    if (!customerEmail || !authToken) return;

    if (!unmountedRef.current) setState({ phase: 'loading' });

    // Resolve email -> UUID on first call (same pattern as useCrateSubscription)
    if (!customerUuidRef.current) {
      try {
        const customers = await queryTable<CustomerRecord>('Customer', { email: customerEmail });
        if (customers.length > 0 && customers[0]) {
          customerUuidRef.current = customers[0].uuid;
        } else {
          if (!unmountedRef.current) setState({ phase: 'success', orders: [] });
          return;
        }
      } catch (err) {
        if (!unmountedRef.current) {
          const msg = err instanceof Error ? err.message : 'Failed to resolve customer account.';
          setState({ phase: 'error', message: msg });
        }
        return;
      }
    }

    try {
      // AC-003: filtered by customer_id - never returns other customers orders
      const uuid = customerUuidRef.current;
      if (!uuid) return;
      const data = await queryTable<Order>('Order', { customer_id: uuid });

      if (!unmountedRef.current) {
        // Sort by scheduled_date descending - most recent first (AC-002)
        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) =>
              (b.scheduled_date ?? '').localeCompare(a.scheduled_date ?? ''),
            )
          : [];
        setState({ phase: 'success', orders: sorted });
      }
    } catch (err) {
      if (!unmountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Could not load orders.';
        setState({ phase: 'error', message: msg });
      }
    }
  }, [customerEmail, authToken]);

  useEffect(() => {
    unmountedRef.current = false;
    void fetchOrders();
    return () => { unmountedRef.current = true; };
  }, [fetchOrders]);

  // Subscribe to Order table changes via WebSocket — refetch on any change
  useHarperSubscription(customerEmail ? 'Order' : null, useCallback(() => {
    void fetchOrders();
  }, [fetchOrders]));

  return {
    orders: state.phase === 'success' ? state.orders : [],
    isLoading: state.phase === 'loading',
    error: state.phase === 'error' ? state.message : null,
    refetch: fetchOrders,
  };
}
